/**
 * Main authentication hook that combines all auth functionality
 */

import { useEffect, useMemo } from 'react';
import { useAuthState, AuthState } from './useAuthState';
import { useAuthActions, AuthActions } from './useAuthActions';
import { useTokenRefresh } from './useTokenRefresh';
import { useUserProfile } from './useUserProfile';
import { analyticsClient } from 'sdks/analytics';

export interface UseAuthReturn extends AuthState, AuthActions {
  isNewUser: boolean;
  needsOnboarding: boolean;
  isProfileLoading: boolean;
}

/**
 * Main authentication hook
 * Combines state management, actions, and token refresh logic
 */
export const useAuth = (): UseAuthReturn => {
  const [state, stateActions] = useAuthState();
  const actions = useAuthActions(stateActions);

  // Set up automatic token refresh
  useTokenRefresh({
    user: state.user,
    refreshAuth: actions.refreshAuth,
  });

  // Use existing useUserProfile hook (only when authenticated)
  const { userProfile, isLoading: isProfileLoading } = useUserProfile({
    enabled: state.isAuthenticated
  });

  // Sync profile from useUserProfile to auth state
  useEffect(() => {
    if (state.isAuthenticated && userProfile) {
      analyticsClient.setUser(userProfile.id);
      stateActions.setProfileDetails(userProfile);
    } else if (!state.isAuthenticated) {
      analyticsClient.setUser(null);
      stateActions.setProfileDetails(null);
    }
  }, [state.isAuthenticated, userProfile, stateActions.setProfileDetails]);

  // Helper properties
  const isNewUser = state.user?.is_new_user || false;
  const needsOnboarding = state.needsOnboarding;

  return useMemo(() => ({
    // State
    user: state.user,
    profileDetails: state.profileDetails,
    isLoading: state.isLoading,
    isProfileLoading: isProfileLoading,
    error: state.error,
    isAuthenticated: state.isAuthenticated,

    // Helper properties
    isNewUser,
    needsOnboarding,

    // Actions
    login: actions.login,
    verifyOtp: actions.verifyOtp,
    logout: actions.logout,
    refreshAuth: actions.refreshAuth,
    setNeedsOnboarding: actions.setNeedsOnboarding,
  }), [state, actions, isNewUser, needsOnboarding, isProfileLoading]);

};
